<template>
  <!--
    Lamps, drawn as symbols rather than illustrations: one silhouette, one line
    of architecture to place it against (the ceiling, the wall, the surface), and
    lamplight when the light is on.

    The body keeps the appliances' glass idiom — a pale shape lifted by the ink it
    is drawn in, so a fitting stays a fitting on a white card — and `currentColor`
    only ever tints the glass. The light itself is warm amber whatever the tile's
    accent is, because a lamp's colour is not the switch's colour. `level` scales
    the light; `active` is the switch.
  -->
  <svg
    class="light-anim"
    :class="[`light-anim--${kind}`, { 'light-anim--on': active }]"
    :style="{ '--light-on': lightLevel }"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <defs>
      <radialGradient :id="glowId" cx="0.5" cy="0.5" r="0.5">
        <stop class="light__halo-core" offset="0%" />
        <stop class="light__halo-mid" offset="52%" />
        <stop class="light__halo-edge" offset="100%" />
      </radialGradient>
      <linearGradient :id="beamId" x1="0" y1="0" x2="0" y2="1">
        <stop class="light__beam-top" offset="0%" />
        <stop class="light__beam-mid" offset="74%" />
        <stop class="light__beam-end" offset="100%" />
      </linearGradient>
    </defs>

    <!-- ── Flush ceiling light: a shallow dome on the ceiling. ────────────── -->
    <g v-if="kind === 'ceiling'">
      <path class="light__arch" d="M7,7 H41" />
      <path class="light__glass" d="M13.6,8 h20.8 a10.4,7.6 0 0 1 -20.8,0 Z" />
      <path class="light__lit" d="M15.8,9.4 h16.4 a8.2,5.8 0 0 1 -16.4,0 Z" :style="{ opacity: litOpacity }" />
      <circle class="light__halo" cx="24" cy="14" r="13" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Pendant: a cone on a cord, its light falling below. ───────────── -->
    <g v-else-if="kind === 'pendant'">
      <path class="light__arch" d="M7,7 H41" />
      <path class="light__cord" d="M24,7 V13.6" />
      <path class="light__glass" d="M16.6,26.4 L20.6,13.6 h6.8 l4,12.8 Z" />
      <path class="light__lit" d="M18.6,24.4 h10.8 v1.8 h-10.8 Z" :style="{ opacity: litOpacity }" />
      <path class="light__beam" d="M18.4,27 L24,42 L29.6,27 Z" :fill="`url(#${beamId})`" />
    </g>

    <!-- ── Chandelier: one bar, three drops, three small shades. ─────────── -->
    <g v-else-if="kind === 'chandelier'">
      <path class="light__arch" d="M7,7 H41" />
      <path class="light__cord" d="M24,7 V12" />
      <rect class="light__glass" x="12" y="12" width="24" height="2.6" rx="1.3" />
      <path class="light__cord" d="M16,14.6 V19.4 M24,14.6 V19.4 M32,14.6 V19.4" />
      <path class="light__glass" d="M13.4,24.6 a3.4,3.4 0 0 0 6.8,0 Z" />
      <path class="light__glass" d="M21.4,24.6 a3.4,3.4 0 0 0 6.8,0 Z" />
      <path class="light__glass" d="M29.4,24.6 a3.4,3.4 0 0 0 6.8,0 Z" />
      <g class="light__lit" :style="{ opacity: litOpacity }">
        <circle cx="16.8" cy="25.8" r="1.1" />
        <circle cx="24.8" cy="25.8" r="1.1" />
        <circle cx="32.8" cy="25.8" r="1.1" />
      </g>
      <circle class="light__halo" cx="24" cy="25" r="15" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Linear: a suspended bar. ──────────────────────────────────────── -->
    <g v-else-if="kind === 'linear'">
      <path class="light__arch" d="M7,7 H41" />
      <path class="light__cord" d="M13,7 V12.4 M35,7 V12.4" />
      <rect class="light__glass" x="8.6" y="12" width="30.8" height="5.2" rx="2.6" />
      <rect class="light__lit" x="10.6" y="13.6" width="26.8" height="2" rx="1" :style="{ opacity: litOpacity }" />
      <ellipse class="light__halo" cx="24" cy="18.6" rx="19" ry="6.4" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Circular: a ring hung on a V of cords. ────────────────────────── -->
    <g v-else-if="kind === 'circular'">
      <path class="light__arch" d="M7,7 H41" />
      <path class="light__cord" d="M24,7 L18.4,16.8 M24,7 L29.6,16.8" />
      <circle class="light__ring" cx="24" cy="27.4" r="10.2" />
      <circle class="light__lit-ring" cx="24" cy="27.4" r="10.2" :style="{ opacity: litOpacity }" />
      <circle class="light__halo" cx="24" cy="27.4" r="16" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Panel: a flat panel set into the ceiling. ─────────────────────── -->
    <g v-else-if="kind === 'panel'">
      <path class="light__arch" d="M7,7 H41" />
      <rect class="light__glass" x="12.6" y="7" width="22.8" height="9.2" rx="1.8" />
      <rect class="light__lit" x="14.4" y="8.6" width="19.2" height="6" rx="1.2" :style="{ opacity: litOpacity }" />
      <ellipse class="light__halo" cx="24" cy="18.4" rx="15.5" ry="6.6" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Downlight: a recessed trim and its cone. ──────────────────────── -->
    <g v-else-if="kind === 'downlight'">
      <path class="light__arch" d="M7,7 H41" />
      <rect class="light__glass" x="16.6" y="5.4" width="14.8" height="3.4" rx="1.4" />
      <ellipse class="light__lit" cx="24" cy="8.6" rx="5.2" ry="1.6" :style="{ opacity: litOpacity }" />
      <path class="light__beam" d="M18.2,9.6 L12.6,38 H35.4 L29.8,9.6 Z" :fill="`url(#${beamId})`" />
    </g>

    <!-- ── Spot: two cans on a track. ────────────────────────────────────── -->
    <g v-else-if="kind === 'spot'">
      <path class="light__arch" d="M7,7 H41" />
      <rect class="light__glass" x="11" y="7.2" width="26" height="2.8" rx="1.4" />
      <rect class="light__glass" x="15.6" y="10" width="6.4" height="7" rx="2.4" />
      <rect class="light__glass" x="26" y="10" width="6.4" height="7" rx="2.4" />
      <ellipse class="light__lit" cx="18.8" cy="17" rx="3" ry="1.2" :style="{ opacity: litOpacity }" />
      <ellipse class="light__lit" cx="29.2" cy="17" rx="3" ry="1.2" :style="{ opacity: litOpacity }" />
      <path class="light__beam" d="M16.8,18.2 L11.6,38 H23.4 L21.2,18.2 Z" :fill="`url(#${beamId})`" />
      <path class="light__beam" d="M26.8,18.2 L24.6,38 H36.4 L31.2,18.2 Z" :fill="`url(#${beamId})`" />
    </g>

    <!-- ── Wall sconce: a shade on an arm, washing the wall below. ───────── -->
    <g v-else-if="kind === 'wall'">
      <path class="light__arch" d="M9,6 V42" />
      <path class="light__line" d="M9,26.4 H15.4" />
      <path class="light__glass" d="M15.4,26.4 a7,7 0 0 1 14,0 Z" />
      <ellipse class="light__lit" cx="22.4" cy="26.6" rx="5.2" ry="1.3" :style="{ opacity: litOpacity }" />
      <path class="light__beam" d="M16.8,27.6 L19.6,40 H25.2 L28,27.6 Z" :fill="`url(#${beamId})`" />
    </g>

    <!-- ── Table lamp: a shade on a short stem, on a surface. ────────────── -->
    <g v-else-if="kind === 'table'">
      <path class="light__arch" d="M11,40.4 H37" />
      <path class="light__glass" d="M16,22.6 L18.8,13.4 h10.4 l2.8,9.2 Z" />
      <path class="light__lit" d="M17,20.6 h14 v1.8 h-14 Z" :style="{ opacity: litOpacity }" />
      <path class="light__line" d="M24,22.6 V35.8" />
      <rect class="light__glass" x="17.6" y="35.8" width="12.8" height="3.4" rx="1.7" />
      <circle class="light__halo" cx="24" cy="19" r="11.5" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Floor lamp: the same shade high on a long stem. ───────────────── -->
    <g v-else-if="kind === 'floor'">
      <path class="light__glass" d="M15,17.4 L17.8,8.6 h12.4 l2.8,8.8 Z" />
      <path class="light__lit" d="M16,15.4 h16 v1.8 h-16 Z" :style="{ opacity: litOpacity }" />
      <path class="light__line" d="M24,17.4 V39" />
      <rect class="light__glass" x="16.6" y="39" width="14.8" height="3.2" rx="1.6" />
      <circle class="light__halo" cx="24" cy="14" r="12" :fill="`url(#${glowId})`" />
    </g>

    <!-- ── Strip: a profile on the wall, washing across it. ──────────────── -->
    <g v-else>
      <path class="light__arch" d="M8,6 V42" />
      <rect class="light__glass" x="9.4" y="12.6" width="2.8" height="22.8" rx="1.4" />
      <rect class="light__lit" x="10.4" y="14" width="0.8" height="20" rx="0.4" :style="{ opacity: litOpacity }" />
      <ellipse class="light__halo" cx="20" cy="24" rx="14" ry="12.5" :fill="`url(#${glowId})`" />
    </g>
  </svg>
</template>

<script lang="ts">
/** Module scope: gradient ids must be unique per instance. */
let instanceSeq = 0;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { LightKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: LightKind;
    /** The light is on — the only thing that turns the lamplight on. */
    active?: boolean;
    /** 0–100 brightness: scales the light, does not gate it. */
    level?: number | null;
  }>(),
  { kind: 'ceiling', active: false, level: null },
);

/** Ids must be unique per instance, or the first gradient wins for every tile. */
const uid = `light-anim-${++instanceSeq}`;
const glowId = `${uid}-glow`;
const beamId = `${uid}-beam`;

/** The level as a fraction; an unreadable level is a comfortable three-quarters. */
const brightness = computed(() => {
  const level = props.level;
  const pct = typeof level === 'number' && Number.isFinite(level) ? Math.min(100, Math.max(0, level)) : 75;
  return pct / 100;
});

/**
 * The light in the air, as a 0–1 fraction the gradient stops scale themselves
 * by: absent when the lamp is off, brighter with the level when it is on.
 */
const lightLevel = computed(() => (props.active ? 0.55 + 0.45 * brightness.value : 0));

/** The light on the fitting itself (the lit shade, lens or mouth). */
const litOpacity = computed(() => (props.active ? 0.5 + 0.5 * brightness.value : 0));
</script>

<style scoped>
.light-anim {
  display: block;
  width: 100%;
  height: 100%;

  /* Glass is fixed and light — the ink only lifts it — and the light is warm on
     every tile, because a lamp is not a switch's accent. */
  --light-glass: color-mix(in srgb, currentColor 10%, rgb(234 239 245));
  --light-edge: color-mix(in srgb, currentColor 26%, rgb(150 165 184));
  --light-line: color-mix(in srgb, currentColor 24%, rgb(120 136 156));
  --light-mute: color-mix(in srgb, currentColor 32%, rgb(148 163 184));
  --light-warm: #fbbf24;
  --light-warm-soft: #fcd34d;
}

/* ── The fitting ────────────────────────────────────────────────────────── */
.light__glass {
  fill: var(--light-glass);
  stroke: var(--light-edge);
  stroke-width: 1.4;
  stroke-linejoin: round;
}

.light__line {
  fill: none;
  stroke: var(--light-line);
  stroke-width: 1.6;
  stroke-linecap: round;
}

/* The cord and the ceiling/wall/surface it hangs from: hairlines, so the
   fitting stays the subject. */
.light__cord {
  fill: none;
  stroke: var(--light-mute);
  stroke-width: 1.3;
  stroke-linecap: round;
}

.light__arch {
  fill: none;
  stroke: var(--light-mute);
  stroke-width: 1.2;
  stroke-linecap: round;
  opacity: 0.7;
}

.light__ring {
  fill: none;
  stroke: var(--light-edge);
  stroke-width: 3.6;
}

/* ── The light ──────────────────────────────────────────────────────────── */
.light__lit,
.light__lit-ring {
  fill: var(--light-warm-soft);
  stroke: none;
  transition: opacity 0.3s ease;
}

.light__lit-ring {
  fill: none;
  stroke: var(--light-warm);
  stroke-width: 3.6;
}

/* The halo and the beam carry their own opacity (the level), so they only
   transition it. */
.light__halo,
.light__beam {
  stroke: none;
  transition: opacity 0.3s ease;
}

/* The gradient stops live in CSS, not attributes: they scale themselves by the
   level (`--light-on`), and the two themes need different stops — on a dark card
   a half-transparent amber composites to muddy brown, so the same light starts
   nearly opaque and pale instead. */
.light__halo-core {
  stop-color: #fde68a;
  stop-opacity: calc(var(--light-on, 0) * 0.9);
}

.light__halo-mid {
  stop-color: #fbbf24;
  stop-opacity: calc(var(--light-on, 0) * 0.3);
}

.light__halo-edge {
  stop-color: #f59e0b;
  stop-opacity: 0;
}

.light__beam-top {
  stop-color: #fbbf24;
  stop-opacity: calc(var(--light-on, 0) * 0.55);
}

.light__beam-mid {
  stop-color: #fbbf24;
  stop-opacity: calc(var(--light-on, 0) * 0.3);
}

.light__beam-end {
  stop-color: #fbbf24;
  stop-opacity: 0;
}

.dark .light__halo-core {
  stop-color: #fef3c7;
  stop-opacity: var(--light-on, 0);
}

.dark .light__halo-mid {
  stop-color: #fbbf24;
  stop-opacity: calc(var(--light-on, 0) * 0.62);
}

.dark .light__beam-top {
  stop-color: #fde68a;
  stop-opacity: var(--light-on, 0);
}

.dark .light__beam-mid {
  stop-color: #fbbf24;
  stop-opacity: calc(var(--light-on, 0) * 0.68);
}

.dark .light__beam-end {
  stop-color: #f59e0b;
  stop-opacity: 0;
}

.light__halo {
  transform-box: fill-box;
  transform-origin: center;
}

/* A lit lamp holds steady: a symbol that flickered would be a fault, not a
   drawing. The halo alone breathes, slowly enough to read as "on". */
.light-anim--on .light__halo {
  animation: light-anim-breathe 4.2s ease-in-out infinite;
}

@keyframes light-anim-breathe {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@media (prefers-reduced-motion: reduce) {
  .light-anim--on .light__halo {
    animation: none;
  }
}
</style>
